import { memo, useCallback } from 'react';
import { CardComponentProps } from './Card.types';
import {
  StyledCard,
  StyledCardHeader,
  StyledCardContent,
  StyledCardActions,
} from './Card.styles';

/**
 * Card Component
 *
 * Professional card component for displaying content in a structured layout.
 * Follows the green/teal design system with elevated, outlined, and flat variants.
 *
 * @example
 * ```tsx
 * // Basic elevated card
 * <Card variant="elevated" title="Employee Profile">
 *   <p>Card content goes here</p>
 * </Card>
 *
 * // Outlined card with actions
 * <Card
 *   variant="outlined"
 *   title="Dashboard Stats"
 *   subtitle="Last updated: 2 hours ago"
 *   actions={<Button size="sm">View Details</Button>}
 * >
 *   <StatsContent />
 * </Card>
 *
 * // Clickable card
 * <Card
 *   variant="elevated"
 *   clickable
 *   onClick={() => navigate('/details')}
 * >
 *   <p>Click to view details</p>
 * </Card>
 * ```
 */
export const Card = memo<CardComponentProps>(
  function Card({
    variant = 'elevated',
    padding = 'md',
    header,
    title,
    subtitle,
    actions,
    children,
    footer,
    clickable = false,
    onClick,
    disabled = false,
    fullHeight = false,
    className,
    'data-testid': dataTestId,
    elevation,
  }) {
    // Determine if card should be clickable
    const isClickable = clickable || !!onClick;

    // Memoize click handler
    const handleClick = useCallback(() => {
      if (!disabled && onClick) {
        onClick();
      }
    }, [disabled, onClick]);

    return (
      <StyledCard
        cardVariant={variant}
        clickable={isClickable}
        fullHeight={fullHeight}
        onClick={isClickable ? handleClick : undefined}
        className={className}
        data-testid={dataTestId}
        elevation={variant === 'elevated' ? elevation : 0}
      >
        {/* Custom header or title/subtitle/actions header */}
        {header ? (
          header
        ) : (title || subtitle || actions) ? (
          <StyledCardHeader
            title={title}
            subheader={subtitle}
            action={actions}
          />
        ) : null}

        {/* Card content */}
        <StyledCardContent cardPadding={padding}>
          {children}
        </StyledCardContent>

        {/* Footer */}
        {footer && <StyledCardActions>{footer}</StyledCardActions>}
      </StyledCard>
    );
  }
);

Card.displayName = 'Card';

export default Card;
